import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useEntryConfigs } from "@/hooks/useFirestore";
import { useRequireSelectedOrgId } from "@/hooks/useRequireSelectedOrgId";
import { db } from "@/lib/firebase";
import { cn } from "@/lib/utils";
import { Task } from "@/types/task";
import { DataCollectionConfig } from "@/types/dataCollectionConfig";
import { doc, DocumentReference, Timestamp } from "firebase/firestore";
import { useTraitSelection } from "@/hooks/useTraitSelection";
import { canEditTaskTraits, decideTraitRemoval, entryConfigIdOf, preservedConfigIds } from "./editTaskTraitRemoval";
import { TraitSelector } from "./TraitSelector";

const taskSchema = z.object({
  name: z.string().min(1, { message: "Task name is required" }),
  description: z.string().optional(),
  startDate: z.date(),
  entryConfigData: z.array(z.object({
    entryConfigId: z.string(),
    minNumSamples: z.number().min(1),
  })).optional(),
});

// A config is GLOBAL when it has no per-user userVisibility. Field-scoped
// (per-user) configs are hidden from this global Edit form and preserved
// verbatim on save — matching iOS (TaskActionHelpers.EditTaskContext).
const isGlobalConfig = (config: DataCollectionConfig) =>
  !(config.userVisibility && config.userVisibility.length > 0);

interface EditTaskFormProps {
  task: Task;
  onSubmit: (updatedTask: Task) => void;
  onCancel: () => void;
  /** Reports whether the form has unsaved edits, so the parent dialog can guard against accidental close. */
  onDirtyChange?: (dirty: boolean) => void;
}

const EditTaskForm: React.FC<EditTaskFormProps> = ({ task, onSubmit, onCancel, onDirtyChange }) => {
  const { user, userRole } = useAuth();
  const requireOrgId = useRequireSelectedOrgId();
  const { toast } = useToast();
  const { entryConfigs, loading: entryConfigsLoading } = useEntryConfigs();

  // Only editors and admins can add new traits (not collectors)
  const canAddTrait = userRole && ["editor", "admin"].includes(userRole);

  // Holds the form data pending a trait-removal confirmation (set when the user
  // tries to save an edit that removes one or more traits). We store the form
  // DATA, not a built task — the update is rebuilt against the LATEST task at
  // confirm time, so a trait another client appends while the dialog is open
  // isn't dropped by a payload frozen at Save-click. The organization is the
  // one captured at Save-click, so the confirmed save builds the same refs.
  const [pendingRemoval, setPendingRemoval] = useState<{
    data: z.infer<typeof taskSchema>;
    removedTraitIds: string[];
    orgId: string;
  } | null>(null);

  // Snapshot of the GLOBAL trait ids the form was opened with. The `task` prop
  // is fed by a live onSnapshot listener and can change while the dialog is
  // open, so removal is detected against this snapshot — not the live task — so
  // a trait added concurrently is neither mis-detected as "removed" nor dropped.
  const [initialGlobalTraitIds] = useState<Set<string>>(
    () => new Set(
      (task.dataCollectionConfigs || [])
        .filter(isGlobalConfig)
        .map((c) => entryConfigIdOf(c.entryConfigRef))
        .filter((id): id is string => Boolean(id))
    )
  );

  // Check if current user is the task creator
  const isTaskCreator = useMemo(() => {
    if (!user || !task.createdBy) return false;
    const creatorPath = typeof task.createdBy === 'string'
      ? task.createdBy
      : task.createdBy.path;
    return creatorPath === `users/${user.uid}`;
  }, [user, task.createdBy]);

  // Whether the user can edit the task's traits — toggle, remove, or change
  // samples. Editors/admins always; a collector only on tasks they created (a
  // collector editing someone else's task can't save it — blocked below — so
  // the controls must not falsely imply the task is editable). Removal is
  // allowed for anyone who can edit: it does NOT delete collected data — the
  // trait's entryCollections are preserved (hidden) and reappear on re-add.
  const canEditTraits = canEditTaskTraits({ userRole, isTaskCreator });

  // Convert the task's GLOBAL configs to form rows. Field-scoped (per-user)
  // configs are intentionally excluded so they can't be edited/removed here.
  const getEntryConfigDataFromTask = (task: Task) => {
    return (task.dataCollectionConfigs || [])
      .filter(isGlobalConfig)
      .map((config) => ({
        entryConfigId: entryConfigIdOf(config.entryConfigRef),
        minNumSamples: config.minNumSamples,
      }))
      .filter((d): d is { entryConfigId: string; minNumSamples: number } => Boolean(d.entryConfigId));
  };

  const form = useForm<z.infer<typeof taskSchema>>({
    resolver: zodResolver(taskSchema),
    defaultValues: {
      name: task.name,
      description: task.description,
      startDate: task.startDate?.toDate() || new Date(),
      entryConfigData: getEntryConfigDataFromTask(task),
    },
  });

  const selectedEntryConfigData = form.watch("entryConfigData") || [];
  const selectedEntryConfigIds = selectedEntryConfigData.map(d => d.entryConfigId);

  // Surface unsaved-edit state so the wrapping dialog can confirm before discarding.
  const isDirty = form.formState.isDirty;
  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const {
    showAddTraitDialog,
    setShowAddTraitDialog,
    toggleEntryConfig,
    updateMinNumSamples,
    handleAddTrait,
  } = useTraitSelection({ form, selectedEntryConfigData });

  // Rebuild the COMPLETE dataCollectionConfigs array. The form edits only GLOBAL
  // traits (rebuilt here without userVisibility); field-scoped (per-user) configs
  // and any trait added concurrently while the form was open are preserved
  // verbatim, deduped so a promoted trait keeps only its global copy. Mirrors
  // iOS (mergedDataCollectionConfigs) — a stale form must not silently strand
  // (hide) a concurrently-added trait's collected data by dropping its config.
  const buildUpdatedTask = (data: z.infer<typeof taskSchema>, orgId: string): Task => {
    const globalConfigs: DataCollectionConfig[] = (data.entryConfigData || []).map((configData) => ({
      entryConfigRef: doc(db, `organizations/${orgId}/entryConfigs/${configData.entryConfigId}`) as DocumentReference,
      minNumSamples: configData.minNumSamples,
    }));

    const keepIds = new Set(
      preservedConfigIds({
        initialGlobalIds: Array.from(initialGlobalTraitIds),
        builtGlobalIds: globalConfigs.map((c) => c.entryConfigRef.id),
        currentConfigs: (task.dataCollectionConfigs || []).map((c) => ({
          id: entryConfigIdOf(c.entryConfigRef) ?? "",
          fieldScoped: !isGlobalConfig(c),
        })),
      })
    );
    const preservedConfigs = (task.dataCollectionConfigs || []).filter((c) =>
      keepIds.has(entryConfigIdOf(c.entryConfigRef) ?? "")
    );

    return {
      ...task,
      name: data.name,
      description: data.description || "",
      startDate: Timestamp.fromDate(data.startDate),
      dataCollectionConfigs: [...globalConfigs, ...preservedConfigs],
    };
  };

  const handleFormSubmit = (data: z.infer<typeof taskSchema>) => {
    const orgId = requireOrgId();
    if (!orgId) return;

    // Block collectors from editing tasks they didn't create (they should use mobile for field data collection)
    if (userRole === 'collector' && !isTaskCreator) {
      toast({
        title: "Permission Denied",
        description: "Data collectors can only edit tasks on the web that they created. Use the mobile app to add traits in the field.",
        variant: "destructive",
      });
      return;
    }

    const updatedTask = buildUpdatedTask(data, orgId);

    const decision = decideTraitRemoval({
      originalTraitIds: Array.from(initialGlobalTraitIds),
      submittedTraitIds: (data.entryConfigData || []).map((d) => d.entryConfigId),
      canRemoveTrait: canEditTraits,
    });

    // Defense-in-depth backstop (Firestore rules are the real gate): block a
    // removal from a user who can't edit this task's traits, even if the UI
    // control was bypassed.
    if (decision.blocked) {
      toast({
        title: "Permission Denied",
        description: "You can only remove traits from tasks you created.",
        variant: "destructive",
      });
      return;
    }

    // Warn + confirm before removal — the data isn't deleted, but the trait
    // disappears from every user's views until re-added (matches iOS).
    if (decision.needsConfirmation) {
      setPendingRemoval({ data, removedTraitIds: decision.removedTraitIds, orgId });
      return;
    }

    onSubmit(updatedTask);
  };

  const removedTraitNames = (pendingRemoval?.removedTraitIds ?? []).map(
    (id) => `"${entryConfigs.find((c) => c.id === id)?.name ?? "Unknown trait"}"`
  );

  return (
    <>
    <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleFormSubmit)}
          className="space-y-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Task Name*</FormLabel>
              <FormControl>
                <Input
                  placeholder="Enter task name"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Describe the task details"
                  className="min-h-[80px] resize-none"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="startDate"
          render={({ field }) => (
            <FormItem className="flex flex-col">
              <FormLabel>Task Start Date*</FormLabel>
              <Popover>
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button
                      variant="outline"
                      className={cn("w-full pl-3 text-left font-normal", !field.value && "text-muted-foreground")}>
                      {field.value ? format(field.value, "PPP") : <span>Select a date</span>}
                      <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                <PopoverContent
                  className="w-auto p-0"
                  align="start">
                  <Calendar
                    mode="single"
                    selected={field.value}
                    onSelect={field.onChange}
                    initialFocus
                    className="p-3 pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
              <FormMessage />
            </FormItem>
          )}
        />

        <TraitSelector
          entryConfigs={entryConfigs}
          entryConfigsLoading={entryConfigsLoading}
          selectedEntryConfigData={selectedEntryConfigData}
          selectedEntryConfigIds={selectedEntryConfigIds}
          onToggleEntryConfig={toggleEntryConfig}
          onUpdateMinNumSamples={updateMinNumSamples}
          showAddTraitDialog={showAddTraitDialog}
          onShowAddTraitDialogChange={setShowAddTraitDialog}
          onTraitAdded={handleAddTrait}
          canAddTrait={canAddTrait}
          editLockedReason={canEditTraits ? undefined : "You can only edit traits on tasks you created."}
        />

        <div className="flex justify-end space-x-2 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="submit"
            className="bg-agri-green hover:bg-agri-green-dark">
            Save Changes
          </Button>
        </div>
      </form>
    </Form>

    <AlertDialog
      open={pendingRemoval !== null}
      onOpenChange={(open) => {
        if (!open) setPendingRemoval(null);
      }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {removedTraitNames.length === 1 ? "Remove trait?" : "Remove traits?"}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {removedTraitNames.join(", ")} will be removed from this task.
            Collected data is not deleted — it is hidden for all users and will
            reappear if the{" "}
            {removedTraitNames.length === 1 ? "trait is" : "traits are"}{" "}
            re-added later.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => setPendingRemoval(null)}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={() => {
              const pending = pendingRemoval;
              setPendingRemoval(null);
              // Rebuild against the LATEST task (preserves any trait appended
              // since Save was clicked) rather than a payload frozen at Save.
              if (pending) onSubmit(buildUpdatedTask(pending.data, pending.orgId));
            }}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
            Remove
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
};

export default EditTaskForm;
